"use client";

import dynamic from "next/dynamic";

import useDeferredLoad from "@/lib/hooks/useDeferredLoad";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface LazyGroupFormProps {
  className?: string;
}

function GroupFormSkeleton({ className }: LazyGroupFormProps) {
  const fieldClassName =
    "flex h-12 w-full border border-input bg-transparent p-5 text-sm shadow-sm";
  const labelClassName = "text-sm font-medium leading-none";

  return (
    <form
      aria-hidden="true"
      inert
      className={`${className ?? ""} mt-10 grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-5`}
    >
      <div className="space-y-2">
        <label className={labelClassName}>First Name</label>
        <Input className={fieldClassName} />
      </div>
      <div className="space-y-2">
        <label className={labelClassName}>Last Name</label>
        <Input className={fieldClassName} />
      </div>
      <div className="space-y-2">
        <label className={labelClassName}>Group Name</label>
        <Input className={fieldClassName} />
      </div>
      <div className="space-y-2">
        <label className={labelClassName}>Phone Number</label>
        <div className="flex">
          <div className="flex h-12 items-center gap-1 rounded-e-none rounded-s-lg border border-r-0 border-gray-200 px-3">
            <div className="h-4 w-6 rounded-sm bg-white/70" />
          </div>
          <Input className="rounded-none rounded-s-none" />
        </div>
      </div>
      <div className="space-y-2">
        <label className={labelClassName}>Email Address</label>
        <Input className={fieldClassName} type="email" />
      </div>
      <div className="space-y-2">
        <label className={labelClassName}>Number of People</label>
        <Input className={fieldClassName} type="number" />
      </div>
      <div className="space-y-2 pb-1 pt-1">
        <label className={`${labelClassName} pb-1`}>Arrival Date</label>
        <div className="flex h-12 items-center border border-white bg-inherit px-3 text-left font-normal text-white">
          <span className="ml-auto h-4 w-4 rounded-full bg-white/50" />
        </div>
      </div>
      <div className="space-y-2 pb-1 pt-1">
        <label className={`${labelClassName} pb-1`}>Departing Date</label>
        <div className="flex h-12 items-center border border-white bg-inherit px-3 text-left font-normal text-white">
          <span className="ml-auto h-4 w-4 rounded-full bg-white/50" />
        </div>
      </div>
      <div className="space-y-2">
        <label className={labelClassName}>Activities You&apos;re Interested In</label>
        <Input className={fieldClassName} />
      </div>
      <div className="space-y-2">
        <label className={labelClassName}>Where Is Your Group Staying?</label>
        <Input className={fieldClassName} />
      </div>
      <div className="space-y-2 lg:col-span-2">
        <label className={labelClassName}>Transportation Required?</label>
        <Input className={fieldClassName} />
      </div>
      <div className="space-y-2 lg:col-span-2">
        <label className={labelClassName}>Additional Comments</label>
        <Textarea className="min-h-[100px] rounded-none border border-input bg-transparent px-3 py-2 text-sm shadow-sm" />
      </div>
      <div className="mt-4 bg-white p-5 py-7 text-center text-lg font-semibold uppercase text-primary-blue lg:col-span-2 lg:mx-auto lg:w-[300px]">
        Submit
      </div>
    </form>
  );
}

const GroupForm = dynamic(() => import("./GroupForm"), {
  ssr: false,
  loading: () => <GroupFormSkeleton />,
});

export default function LazyGroupForm({ className }: LazyGroupFormProps) {
  const { ref, shouldLoad } = useDeferredLoad({
    rootMargin: "500px 0px",
    idleMs: 4000,
  });

  return (
    <div ref={ref}>
      {shouldLoad ? (
        <GroupForm className={className} />
      ) : (
        <GroupFormSkeleton className={className} />
      )}
    </div>
  );
}
